import type { TableColumnStaticProps } from '@/types/tableTypes';
import { fieldKindFor, propertyLabel } from '@/components/record/fields/fieldKind';
import { isMissingFromSearchDocument } from '@/composables/searchInspectorFieldGroups';
import { isNeverShownField } from '@/composables/entityColumns';
import {
    DISPLAY_NAME_FIELD,
    fieldLabelsOf,
    HEADING_ITEMS,
    RECORD_FIELD,
    topLineOf,
    withNewSection,
    withSectionFields,
    withTitle,
    withTopLine,
    type RecordLayoutDefinition,
} from '@/composables/recordLayout/recordLayoutDefinition';

/**
 * The Customize dialog's one list, worked out from a record layout.
 *
 * The list is made of groups, in this order: Title, Top line, Chips, one group
 * per Details section, and Not shown. A field shows on the page when it sits in
 * any group but Not shown. Moving a field is taking it out of one group and
 * putting it into another, so every change here returns a new layout of the
 * same shape. Nothing in this file saves anything.
 *
 * Spec: docs/specs/record-header-layout.md, "Customize, in the shape of
 * Macro's Customize sidebar".
 */

export type CustomizeGroupKind = 'title' | 'top_line' | 'chips' | 'section' | 'not_shown';

/** A group's id: `title`, `top_line`, `chips`, `not_shown`, or `section:<key>` for a Details section. */
export type CustomizeGroupId = string;

export const TITLE_GROUP = 'title';
export const TOP_LINE_GROUP = 'top_line';
export const CHIPS_GROUP = 'chips';
export const NOT_SHOWN_GROUP = 'not_shown';

export function sectionGroupId(sectionKey: string): CustomizeGroupId {
    return `section:${sectionKey}`;
}

function sectionKeyOf(groupId: CustomizeGroupId): string | null {
    return groupId.startsWith('section:') ? groupId.slice('section:'.length) : null;
}

export interface CustomizeRow {
    groupId: CustomizeGroupId;
    field: string;
    /** The name the row shows: the name the person gave it, else the field's own. */
    label: string;
    /** The field's own name, as Details shows it before any rename. */
    ownName: string;
    renamed: boolean;
    /** Saved in the layout, but the record type has no such field any more. */
    missing: boolean;
}

export interface CustomizeGroup {
    id: CustomizeGroupId;
    kind: CustomizeGroupKind;
    /** "Top line", or a section's name such as "Account". */
    label: string;
    /** The Details section's key, for a section group. */
    sectionKey?: string;
    rows: CustomizeRow[];
}

/** Where a row was put: in which group, and before which field. Null means at the end. */
export interface CustomizeDrop {
    groupId: CustomizeGroupId;
    beforeField: string | null;
}

const headingItemNames = new Map(HEADING_ITEMS.map((item) => [item.id, item.label]));

/** Turns field ids into names and rows, for one record type. */
export function rowNamer(entityId: string, columns: TableColumnStaticProps[]) {
    const columnsById = new Map(columns.map((column) => [column.id, column]));

    /**
     * A field's own name, as the place it shows draws it. Details, and so the
     * Columns panel, use the sentence-case name, such as "Status". A chip
     * draws the column's name, such as "Account Status", so a chip's row says
     * what the chip says.
     */
    function ownNameOf(field: string, style: 'details' | 'chip' = 'details'): string {
        const column = columnsById.get(field);
        if (column) return style === 'chip' ? column.header : propertyLabel(entityId, column, fieldKindFor(entityId, column));
        return headingItemNames.get(field) ?? field;
    }

    function isKnown(field: string): boolean {
        return columnsById.has(field) || headingItemNames.has(field);
    }

    function rowsOf(groupId: CustomizeGroupId, fields: string[], givenNames: Record<string, string>): CustomizeRow[] {
        // A layout saved before a field became secret or retired may still name it. It is never shown.
        return fields.filter((field) => !isNeverShownField(entityId, field)).map((field) => {
            const ownName = ownNameOf(field, groupId === CHIPS_GROUP ? 'chip' : 'details');
            const given = givenNames[field];
            return { groupId, field, label: given ?? ownName, ownName, renamed: given !== undefined, missing: !isKnown(field) };
        });
    }

    return { ownNameOf, rowsOf, columnsById };
}

/** "Record type and ID", and "Display name" where it is not a real column. Neither is a column. */
function isHeadingItem(field: string, columns: TableColumnStaticProps[]): boolean {
    return field === RECORD_FIELD || (field === DISPLAY_NAME_FIELD && !columns.some((column) => column.id === field));
}

/**
 * Whether a field may sit in a group. The title, the top line and Not shown
 * take any field. "Record type and ID" and "Display name" are not columns, so
 * the chips and Details cannot draw them.
 */
export function canPlace(field: string, groupId: CustomizeGroupId, columns: TableColumnStaticProps[]): boolean {
    if (groupId === NOT_SHOWN_GROUP || groupId === TOP_LINE_GROUP || groupId === TITLE_GROUP) return true;
    return !isHeadingItem(field, columns);
}

/**
 * The fields the Title group holds. A layout with no `title` shows an empty
 * group, because an emptied title is saved by leaving `title` out. The
 * heading then falls back to the display name, and the dialog says so.
 */
function titleFieldsOf(layout: RecordLayoutDefinition): string[] {
    return layout.title ?? [];
}

/** Every group of the list, in order. */
export function customizeGroups(
    entityId: string,
    layout: RecordLayoutDefinition,
    columns: TableColumnStaticProps[],
): CustomizeGroup[] {
    const { rowsOf, ownNameOf } = rowNamer(entityId, columns);
    const givenNames = fieldLabelsOf(layout);
    // The top line and the title show values only, so they show the field's own name.
    const noGivenNames: Record<string, string> = {};
    const title = titleFieldsOf(layout);
    const topLine = topLineOf(layout, columns);

    const placed = new Set<string>([...title, ...topLine, ...layout.header, ...layout.sections.flatMap((section) => section.fields)]);
    const notShownFields = [
        ...HEADING_ITEMS.map((item) => item.id).filter((field) => !columns.some((column) => column.id === field)),
        // A related field the search document never holds would always read "—", so it is not offered.
        ...columns
            .filter((column) => column.dataType !== 'display' && !isMissingFromSearchDocument(entityId, column))
            .map((column) => column.id),
    ]
        .filter((field) => !placed.has(field))
        .sort((first, second) => ownNameOf(first).localeCompare(ownNameOf(second)));

    return [
        { id: TITLE_GROUP, kind: 'title', label: 'Title', rows: rowsOf(TITLE_GROUP, title, noGivenNames) },
        { id: TOP_LINE_GROUP, kind: 'top_line', label: 'Top line', rows: rowsOf(TOP_LINE_GROUP, topLine, noGivenNames) },
        { id: CHIPS_GROUP, kind: 'chips', label: 'Chips', rows: rowsOf(CHIPS_GROUP, layout.header, givenNames) },
        ...layout.sections.map((section) => ({
            id: sectionGroupId(section.key),
            kind: 'section' as const,
            label: section.label,
            sectionKey: section.key,
            rows: rowsOf(sectionGroupId(section.key), section.fields, givenNames),
        })),
        { id: NOT_SHOWN_GROUP, kind: 'not_shown', label: 'Not shown', rows: rowsOf(NOT_SHOWN_GROUP, notShownFields, noGivenNames) },
    ];
}

/** The fields a group holds in the layout. Not shown is not a stored list. */
function fieldsOf(layout: RecordLayoutDefinition, columns: TableColumnStaticProps[], groupId: CustomizeGroupId): string[] {
    if (groupId === TITLE_GROUP) return titleFieldsOf(layout);
    if (groupId === TOP_LINE_GROUP) return topLineOf(layout, columns);
    if (groupId === CHIPS_GROUP) return layout.header;
    const sectionKey = sectionKeyOf(groupId);
    return layout.sections.find((section) => section.key === sectionKey)?.fields ?? [];
}

function withFields(layout: RecordLayoutDefinition, groupId: CustomizeGroupId, fields: string[]): RecordLayoutDefinition {
    if (groupId === TITLE_GROUP) return withTitle(layout, fields);
    if (groupId === TOP_LINE_GROUP) return withTopLine(layout, fields);
    if (groupId === CHIPS_GROUP) return { ...layout, header: fields };
    const sectionKey = sectionKeyOf(groupId);
    return sectionKey === null ? layout : withSectionFields(layout, sectionKey, fields);
}

/**
 * The group a field ticked under Not shown goes to. "Display name" goes to
 * the title and "Record type and ID" to the top line, the places each belongs.
 * Any other field goes to the last Details section.
 */
function tickTarget(layout: RecordLayoutDefinition, field: string, columns: TableColumnStaticProps[]): { layout: RecordLayoutDefinition; groupId: CustomizeGroupId } {
    if (field === DISPLAY_NAME_FIELD && isHeadingItem(field, columns)) return { layout, groupId: TITLE_GROUP };
    if (isHeadingItem(field, columns)) return { layout, groupId: TOP_LINE_GROUP };
    if (layout.sections.length > 0) return { layout, groupId: sectionGroupId(layout.sections[layout.sections.length - 1].key) };
    // A layout with no section at all gets one, so the field has somewhere to go.
    const { layout: withSection, key } = withNewSection(layout, 'Details');
    return { layout: withSection, groupId: sectionGroupId(key) };
}

/**
 * Moves a field from one group to a place in another, or to another place in
 * its own group. Dropping it on Not shown takes it out of the group it was
 * in. Returns null when the move is refused, such as "Record type and ID"
 * into the chips.
 */
export function placeField(
    layout: RecordLayoutDefinition,
    columns: TableColumnStaticProps[],
    field: string,
    fromGroupId: CustomizeGroupId,
    drop: CustomizeDrop,
): RecordLayoutDefinition | null {
    if (!canPlace(field, drop.groupId, columns)) return null;
    if (drop.groupId === NOT_SHOWN_GROUP) {
        return fromGroupId === NOT_SHOWN_GROUP ? null : untickField(layout, columns, field, fromGroupId);
    }

    let next = layout;
    if (fromGroupId !== NOT_SHOWN_GROUP && fromGroupId !== drop.groupId) {
        next = withFields(next, fromGroupId, fieldsOf(next, columns, fromGroupId).filter((candidate) => candidate !== field));
    }
    const target = fieldsOf(next, columns, drop.groupId).filter((candidate) => candidate !== field);
    const beforeIndex = drop.beforeField === null ? -1 : target.indexOf(drop.beforeField);
    target.splice(beforeIndex === -1 ? target.length : beforeIndex, 0, field);
    return withFields(next, drop.groupId, target);
}

/** Ticks a field under Not shown. It goes to the end of the group {@see tickTarget} picks. */
export function tickField(layout: RecordLayoutDefinition, columns: TableColumnStaticProps[], field: string): RecordLayoutDefinition {
    const { layout: next, groupId } = tickTarget(layout, field, columns);
    return placeField(next, columns, field, NOT_SHOWN_GROUP, { groupId, beforeField: null }) ?? layout;
}

/** Unticks a field in a group, so the group no longer holds it. An emptied title falls back to the display name. */
export function untickField(
    layout: RecordLayoutDefinition,
    columns: TableColumnStaticProps[],
    field: string,
    fromGroupId: CustomizeGroupId,
): RecordLayoutDefinition {
    if (fromGroupId === NOT_SHOWN_GROUP) return layout;
    return withFields(layout, fromGroupId, fieldsOf(layout, columns, fromGroupId).filter((candidate) => candidate !== field));
}

/**
 * Moves a field one row up or down with the arrow keys, across groups. The
 * groups are the ones on screen, so a search that hides rows is respected.
 * Past the top of a group the field goes to the end of the group above; past
 * the bottom, to the start of the group below. Groups that cannot take the
 * field are skipped. Returns the new layout and the group the field is now
 * in, or null when it cannot move further.
 */
export function moveFieldOneRow(
    layout: RecordLayoutDefinition,
    columns: TableColumnStaticProps[],
    visibleGroups: CustomizeGroup[],
    field: string,
    fromGroupId: CustomizeGroupId,
    direction: -1 | 1,
): { layout: RecordLayoutDefinition; groupId: CustomizeGroupId } | null {
    const groupIndex = visibleGroups.findIndex((group) => group.id === fromGroupId);
    if (groupIndex < 0) return null;
    const group = visibleGroups[groupIndex];
    const rowIndex = group.rows.findIndex((row) => row.field === field);
    if (rowIndex < 0) return null;

    // Within its own group. Not shown keeps its A to Z order, so it does not reorder.
    const neighbourIndex = rowIndex + direction;
    if (fromGroupId !== NOT_SHOWN_GROUP && neighbourIndex >= 0 && neighbourIndex < group.rows.length) {
        const beforeField = direction === -1 ? group.rows[neighbourIndex].field : (group.rows[neighbourIndex + 1]?.field ?? null);
        const next = placeField(layout, columns, field, fromGroupId, { groupId: fromGroupId, beforeField });
        return next ? { layout: next, groupId: fromGroupId } : null;
    }

    // Up out of Not shown: ticked, to the end of the last Details section.
    if (fromGroupId === NOT_SHOWN_GROUP) {
        if (direction === 1) return null;
        const { groupId } = tickTarget(layout, field, columns);
        return { layout: tickField(layout, columns, field), groupId };
    }

    for (let index = groupIndex + direction; index >= 0 && index < visibleGroups.length; index += direction) {
        const target = visibleGroups[index];
        if (!canPlace(field, target.id, columns)) continue;
        if (target.id === NOT_SHOWN_GROUP) {
            return { layout: untickField(layout, columns, field, fromGroupId), groupId: NOT_SHOWN_GROUP };
        }
        const beforeField = direction === 1 ? (target.rows[0]?.field ?? null) : null;
        const next = placeField(layout, columns, field, fromGroupId, { groupId: target.id, beforeField });
        return next ? { layout: next, groupId: target.id } : null;
    }
    return null;
}

/** Moves a Details section one place up or down. */
export function moveSectionOnePlace(layout: RecordLayoutDefinition, sectionKey: string, direction: -1 | 1): RecordLayoutDefinition {
    const sections = [...layout.sections];
    const from = sections.findIndex((section) => section.key === sectionKey);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= sections.length) return layout;
    [sections[from], sections[to]] = [sections[to], sections[from]];
    return { ...layout, sections };
}

/**
 * Puts a dragged section after the section above where it was dropped, or
 * first when no section is above it.
 */
export function placeSectionAfter(layout: RecordLayoutDefinition, sectionKey: string, afterSectionKey: string | null): RecordLayoutDefinition {
    const moving = layout.sections.find((section) => section.key === sectionKey);
    if (!moving || afterSectionKey === sectionKey) return layout;
    const rest = layout.sections.filter((section) => section.key !== sectionKey);
    const afterIndex = afterSectionKey === null ? -1 : rest.findIndex((section) => section.key === afterSectionKey);
    rest.splice(afterIndex + 1, 0, moving);
    return { ...layout, sections: rest };
}

/** Keeps the rows whose name, given or own, contains the text, ignoring case. A group left with none is dropped. */
export function filterGroups(groups: CustomizeGroup[], query: string): CustomizeGroup[] {
    const needle = query.trim().toLowerCase();
    if (needle === '') return groups;
    return groups
        .map((group) => ({
            ...group,
            rows: group.rows.filter((row) => row.label.toLowerCase().includes(needle) || row.ownName.toLowerCase().includes(needle)),
        }))
        .filter((group) => group.rows.length > 0);
}

/** How many different fields the list holds, for "Search 42 fields". */
export function fieldCountOf(groups: CustomizeGroup[]): number {
    return new Set(groups.flatMap((group) => group.rows.map((row) => row.field))).size;
}
